import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { useInView } from '../../../hooks/useInView';
import { useAuthStore } from '../../../store/useAuthStore';
import { apiClient } from '../../../lib/axios';
import { submitPayuForm } from '../../../lib/payu';
import { openRazorpayCheckout } from '../../../lib/razorpay';
import { ApiPlan } from '../../../lib/plans';

interface PlanDef {
  id: string; // must match the backend plan id (starter / growth / scale)
  name: string;
  price: string;
  credits: string;
  perCredit: string;
  savingsLabel?: string;
  features: string[];
  comingSoon?: string[];
  featured?: boolean;
}

const PLANS: PlanDef[] = [
  {
    id: 'starter',
    name: 'Starter',
    price: '$9',
    credits: '1,000 credits',
    perCredit: '$0.009',
    features: [
      '1,000 credits',
      'Every contact found per search',
      'Name and job title',
      'CSV export',
      '30-day auto data deletion',
    ],
  },
  {
    id: 'growth',
    name: 'Growth',
    price: '$25',
    credits: '5,000 credits',
    perCredit: '$0.005',
    savingsLabel: 'save 44%',
    features: [
      '5,000 credits',
      'Everything in Starter',
      'CSV bulk upload',
    ],
    comingSoon: ['Verified email', 'Verified mobile number'],
  },
  {
    id: 'scale',
    name: 'Scale',
    price: '$40',
    credits: '10,000 credits',
    perCredit: '$0.004',
    savingsLabel: 'save 56%',
    featured: true,
    features: [
      '10,000 credits',
      'Everything in Growth',
      'Lowest rate available',
      'Priority support',
      'API access',
    ],
    comingSoon: [
      'Verified email',
      'Verified mobile number',
      'Best time to call',
      'Customer sentiment',
      'Website quality score',
      'AI-written opening line',
      'AI deep-search',
    ],
  },
];

export default function Pricing() {
  const [hovered, setHovered] = useState<number | null>(null);
  const [buyingId, setBuyingId] = useState<string | null>(null);
  const [customPlans, setCustomPlans] = useState<ApiPlan[]>([]);
  const { isAuthenticated } = useAuthStore();
  const { ref, inView } = useInView(0.08);

  useEffect(() => {
    if (!isAuthenticated) return;
    apiClient.get('/user/custom-plans').then((res) => setCustomPlans(res.data as ApiPlan[])).catch(() => {});
  }, [isAuthenticated]);

  const startCheckout = async (planId: string) => {
    setBuyingId(planId);
    try {
      const res = await apiClient.post('/payment/create-order', { plan_id: planId });
      const data = res.data;

      if (data.gateway === 'razorpay') {
        const payment = await openRazorpayCheckout(data);
        await apiClient.post('/payment/razorpay/verify', payment);
        toast.success('Payment successful! Your credits have been added.');
        setBuyingId(null);
        return;
      }

      toast.message('Redirecting to secure checkout…');
      submitPayuForm(data.action_url, data.params);
    } catch (e: any) {
      if (e?.message === 'Payment cancelled') { setBuyingId(null); return; }
      toast.error(e?.response?.data?.error || e?.response?.data?.message || 'Could not start checkout.');
      setBuyingId(null);
    }
  };

  return (

    <section id="pricing" ref={ref as React.RefObject<HTMLElement>} className="bg-white py-24 sm:py-36 overflow-hidden">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">

        {/* header */}
        <div
          className="mb-16"
          style={{ opacity: inView ? 1 : 0, transform: inView ? 'translateY(0)' : 'translateY(24px)', transition: 'opacity 0.6s ease' }}
        >
          <p className="text-[11px] font-black uppercase tracking-[0.18em] text-brand-700 mb-4">Pricing</p>
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <h2 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-[-0.04em] text-slate-900 leading-[0.95]">
              Pay per company.<br />
              <span className="text-slate-400">Never per contact.</span>
            </h2>
            <p className="text-[15px] text-slate-400 max-w-[300px] sm:text-right leading-relaxed flex-shrink-0">
              One credit = one company. All contacts included, no cap per search.
            </p>
          </div>
        </div>

        {/* plans grid — items-stretch so all cards are equal height */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 items-stretch">
          {PLANS.map((plan, i) => {
            const isHov = hovered === i;
            const isScale = !!plan.featured;

            return (
              <div
                key={plan.name}
                className="relative flex flex-col rounded-3xl overflow-hidden cursor-default transition-all duration-300 bg-white"
                style={{
                  opacity: inView ? 1 : 0,
                  transform: inView
                    ? isScale
                      ? isHov ? 'translateY(-10px) scale(1.02)' : 'translateY(-6px) scale(1.01)'
                      : isHov ? 'translateY(-4px)' : 'translateY(0)'
                    : 'translateY(32px)',
                  border: isScale
                    ? '2px solid #22c55e'
                    : isHov
                      ? '2px solid #cbd5e1'
                      : '2px solid #e2e8f0',
                  boxShadow: isScale
                    ? isHov
                      ? '0 32px 64px -12px rgba(34,197,94,0.25), 0 8px 24px rgba(0,0,0,0.12)'
                      : '0 20px 48px -8px rgba(34,197,94,0.18), 0 8px 24px rgba(0,0,0,0.1)'
                    : isHov
                      ? '0 12px 32px -8px rgba(0,0,0,0.12)'
                      : '0 2px 8px rgba(0,0,0,0.05)',
                  transition: `opacity 0.5s ease ${i * 120}ms, transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease`,
                }}
                onMouseEnter={() => setHovered(i)}
                onMouseLeave={() => setHovered(null)}
              >

                <div className="p-7 flex flex-col flex-1">

                  {/* name + badge */}
                  <div className="flex items-center justify-between mb-5">
                    <span className={`text-[11px] font-black uppercase tracking-widest ${isScale ? 'text-emerald-600' : 'text-slate-400'}`}>
                      {plan.name}
                    </span>
                    {isScale && (
                      <span className="text-[10px] font-black uppercase tracking-wide bg-emerald-500 text-white px-2.5 py-1 rounded-full">
                        Most popular
                      </span>
                    )}
                  </div>

                  {/* price */}
                  <div className="mb-1.5">
                    <span className="text-[4rem] font-black tracking-tighter leading-none text-slate-900">
                      {plan.price}
                    </span>
                  </div>

                  {/* credits + savings */}
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="text-[13.5px] font-semibold text-slate-500">
                      {plan.credits}
                    </span>
                    {plan.savingsLabel && (
                      <span className={`text-[11px] font-black rounded-full px-2 py-0.5 ${
                        isScale ? 'text-green-800 bg-green-500/15' : 'text-brand-700 bg-brand-500/10'
                      }`}>
                        {plan.savingsLabel}
                      </span>
                    )}
                  </div>

                  {/* per credit */}
                  <p className="text-[12.5px] font-medium mb-6 text-slate-400">
                    <span className={`font-black ${isScale ? 'text-emerald-600' : 'text-slate-800'}`}>{plan.perCredit}</span> per credit
                  </p>

                  {/* divider */}
                  <div className="h-px mb-5 bg-slate-100" />

                  {/* live features */}
                  <ul className="flex flex-col gap-2.5 mb-5">
                    {plan.features.map((f, j) => (
                      <li key={j} className="flex items-start gap-3 text-[13.5px] font-medium">
                        <svg className="w-4 h-4 flex-shrink-0 mt-0.5 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-slate-700">{f}</span>
                      </li>
                    ))}
                  </ul>

                  {/* coming soon features */}
                  {plan.comingSoon && plan.comingSoon.length > 0 && (
                    <>
                      <p className="text-[9.5px] font-black uppercase tracking-widest mb-2.5 text-slate-300">
                        Coming soon
                      </p>
                      <ul className="flex flex-col gap-2 mb-5">
                        {plan.comingSoon.map((f, j) => (
                          <li key={j} className="flex items-start gap-2.5 text-[13px] text-slate-400">
                            <span className="flex-shrink-0 mt-px text-[10px] leading-[1.4]">○</span>
                            {f}
                          </li>
                        ))}
                      </ul>
                    </>
                  )}

                  {/* spacer pushes CTA to bottom */}
                  <div className="flex-1" />

                  {/* CTA — logged-in users pay via PayU for this plan; guests sign up */}
                  {(() => {
                    const ctaClass = `w-full text-center font-black text-[14.5px] py-4 rounded-2xl transition-all duration-200 active:scale-[0.97] block ${
                      isScale
                        ? 'bg-emerald-500 text-white hover:bg-emerald-400 shadow-lg shadow-emerald-500/20'
                        : 'bg-slate-100 border-2 border-slate-200 text-slate-900 hover:bg-slate-900 hover:text-white hover:border-slate-900'
                    }`;
                    return isAuthenticated ? (
                      <button
                        onClick={() => startCheckout(plan.id)}
                        disabled={buyingId !== null}
                        className={`${ctaClass} disabled:opacity-60 disabled:cursor-not-allowed`}
                      >
                        {buyingId === plan.id ? 'Redirecting…' : 'Get started'}
                      </button>
                    ) : (
                      <Link to="/register" className={ctaClass}>
                        Get started
                      </Link>
                    );
                  })()}
                </div>
              </div>
            );
          })}
        </div>

        {/* Custom plans for this user */}
        {customPlans.length > 0 && (
          <div className="mt-12">
            <p className="text-[11px] font-black uppercase tracking-[0.18em] text-emerald-600 mb-6 text-center">Plans available for you</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 items-stretch">
              {customPlans.map((plan) => (
                <div
                  key={plan.id}
                  className="relative flex flex-col rounded-3xl overflow-hidden bg-white"
                  style={{ border: '2px solid #22c55e', boxShadow: '0 4px 16px rgba(34,197,94,0.12)' }}
                >
                  <div className="p-7 flex flex-col flex-1">
                    <div className="flex items-center justify-between mb-5">
                      <span className="text-[11px] font-black uppercase tracking-widest text-emerald-600">{plan.name}</span>
                      <span className="text-[10px] font-black uppercase tracking-wide bg-emerald-500 text-white px-2.5 py-1 rounded-full">For you</span>
                    </div>
                    <div className="mb-1.5">
                      <span className="text-[4rem] font-black tracking-tighter leading-none text-slate-900">
                        ${Number(plan.price_monthly).toFixed(0)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mb-6">
                      <span className="text-[13.5px] font-semibold text-slate-500">{Number(plan.credits).toLocaleString()} credits</span>
                    </div>
                    <div className="flex-1" />
                    <button
                      onClick={() => startCheckout(plan.id)}
                      disabled={buyingId !== null}
                      className="w-full text-center font-black text-[14.5px] py-4 rounded-2xl bg-emerald-500 text-white hover:bg-emerald-400 shadow-lg shadow-emerald-500/20 transition-all duration-200 active:scale-[0.97] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {buyingId === plan.id ? 'Redirecting…' : 'Buy Now'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <p className="text-[12.5px] text-slate-400 mt-10 max-w-[520px] leading-relaxed mx-auto text-center">
          A standard search costs 1 credit. Deep-search and enrichments are priced separately. Credits expire 60 days from purchase.
        </p>

      </div>
    </section>
  );
}
